import React, {KeyboardEventHandler, ReactNode, useEffect, useMemo, useRef, useState} from 'react';
import {Input} from '~/components/Controls/Input';
import {ISelectItem} from '~/components/Controls/Select/common';
import {defaultNumericSort} from '~/components/Controls/Select/utils';
import {DropDown} from '~/components/DropDown';
import {IWithPopper} from '~/components/Popper';
import {QueryList} from '~/components/QueryList';
import {cx} from '~/lib/cx';
import {Intent} from '~/lib/intent';
import style from './index.module.scss';

interface ISelectedItemContentProps<T> {
    selectedItem: ISelectItem<T>;
    onRemove: (item: ISelectItem<T>) => void;
}

const SelectedItemContent = <T,>({selectedItem, onRemove}: ISelectedItemContentProps<T>) => (
    <>
        {selectedItem.prefix && <span className={style.selectSelectedPrefix}>{selectedItem.prefix}</span>}
        <span className={style.selectSelectedLabel}>{selectedItem.label}</span>
        <i className={cx('fa fa-close', style.selectSelectedRemove)}
            onClick={event => {
                event.preventDefault();
                event.stopPropagation();
                onRemove(selectedItem);
            }}/>
    </>
);

export interface IMultiSelectProps<T> extends IWithPopper {
    items: ISelectItem<T>[];
    intent?: Intent;
    placeholder?: string;
    value: T[] | null;
    disabled?: boolean;
    onChange: (value: T[]) => void;
    searchable?: boolean;
    selectAll?: boolean;
    combobox?: boolean;
    autoFocus?: boolean;
    clearable?: boolean;
    className?: string;
    rightIcon?: ReactNode;
    columns?: boolean;
    sortSelected?: (a: ISelectItem<T>, b: ISelectItem<T>) => number;
    collapsible?: boolean;
    selectedItemsMaxHeight?: number;
}

export const MultiSelectInput = <T extends any>({
    items,
    value,
    rightIcon,
    usePopper,
    disabled,
    popperOptions,
    className,
    intent,
    placeholder,
    searchable,
    selectAll,
    combobox,
    autoFocus,
    onChange,
    clearable,
    columns,
    sortSelected = defaultNumericSort,
    collapsible,
    selectedItemsMaxHeight,
}: IMultiSelectProps<T>) => {
    const [open, setOpen] = useState<boolean>(false);
    const [query, setQuery] = useState<string>('');
    const [activeIndex, setActiveIndex] = useState<number>(-1);
    const activeItemRef = useRef<HTMLDivElement>(null);
    const comboboxInputRef = useRef<HTMLInputElement>(null);

    const isCombobox = Boolean(combobox && searchable && !disabled);

    const safeValue = useMemo(() => value ?? [], [value]);

    const selectedItems = useMemo(
        () => (items || [])
            .filter(item => safeValue.includes(item.value))
            .sort(columns ? sortSelected : () => 0),
        [items, safeValue, columns, sortSelected]
    );

    const toSelectItems = useMemo(
        () => (items || []).filter(item => !safeValue.includes(item.value)),
        [items, safeValue]
    );

    const filterItem = (item: ISelectItem<T>) => {
        const q = query.toLocaleLowerCase();
        return item.label.toLocaleLowerCase().includes(q)
            || (item.prefix?.toLocaleLowerCase().includes(q) ?? false);
    };

    const filteredItems = useMemo(
        () => toSelectItems.filter(filterItem),
        [toSelectItems, query]
    );

    useEffect(() => {
        setActiveIndex(-1);
    }, [query, open]);

    useEffect(() => {
        activeItemRef.current?.scrollIntoView({block: 'nearest'});
    }, [activeIndex]);

    useEffect(() => {
        if (!open) {
            setQuery('');
        }
    }, [open]);

    useEffect(() => {
        if (isCombobox && open) {
            comboboxInputRef.current?.focus({preventScroll: true});
        }
    }, [open, isCombobox]);

    const onSelectItem = (item: ISelectItem<T>) => {
        onChange(safeValue.concat(item.value));
    };
    const onRemoveItem = (item: ISelectItem<T>) => onChange(safeValue.filter(v => v !== item.value));
    const allSelected = items.length > 0 && items.every(item => safeValue.includes(item.value));
    const onToggleAll = () => onChange(allSelected ? [] : items.map(item => item.value));

    const handleKeyDown: KeyboardEventHandler<HTMLInputElement> = (event) => {
        if (event.key === 'ArrowDown') {
            event.preventDefault();
            if (!open) {
                setOpen(true);
            }
            if (filteredItems.length) {
                setActiveIndex(index => Math.min(index + 1, filteredItems.length - 1));
            }
        } else if (event.key === 'ArrowUp') {
            event.preventDefault();
            setActiveIndex(index => Math.max(index - 1, 0));
        } else if (event.key === 'Enter') {
            event.preventDefault();
            if (activeIndex >= 0 && activeIndex < filteredItems.length) {
                onSelectItem(filteredItems[activeIndex]);
            }
        } else if (event.key === 'Backspace' && !query && selectedItems.length) {
            onRemoveItem(selectedItems[selectedItems.length - 1]);
        }
    };

    const itemsList = isCombobox ? filteredItems : toSelectItems;
    const selectedItemsStyle = selectedItemsMaxHeight === undefined
        ? undefined
        : {maxHeight: selectedItemsMaxHeight, overflowY: 'auto' as const};

    const selectedContent = collapsible
        ? <div className={style.selectSelectedItems} style={selectedItemsStyle}>
            {selectedItems.length === 0 &&
                <span className={style.selectPlaceholder}>{placeholder}</span>}
            {selectedItems.length === 1 &&
                <span className={style.selectSelectedItem}>
                    <SelectedItemContent selectedItem={selectedItems[0]} onRemove={onRemoveItem}/>
                </span>}
            {selectedItems.length > 1 &&
                <span className={style.selectCount}>{selectedItems.length} selected</span>}
        </div>
        : <div className={cx(style.selectSelectedItems, columns && style.selectSelectedItemsColumns)}
            style={selectedItemsStyle}>
            {selectedItems.length > 0
                ? selectedItems.map(selectedItem => (
                    columns
                        ? <div key={String(selectedItem.value)} className={style.selectColumnItem}>
                            <span className={style.selectSelectedItem}>
                                <SelectedItemContent selectedItem={selectedItem} onRemove={onRemoveItem}/>
                            </span>
                          </div>
                        : <span key={String(selectedItem.value)} className={style.selectSelectedItem}>
                            <SelectedItemContent selectedItem={selectedItem} onRemove={onRemoveItem}/>
                          </span>
                ))
                : <span className={style.selectPlaceholder}>{placeholder}</span>
            }
        </div>;

    return <DropDown rightIcon={rightIcon} usePopper={usePopper} disabled={disabled} popperOptions={popperOptions}
        tabIndex={isCombobox ? -1 : undefined}
        content={() => <>
            {(collapsible && selectedItems.length > 1) &&
                <div className={cx(style.selectSelectedItems, style.selectPanelSelected)}>
                    {selectedItems.map(selectedItem =>
                        <span key={String(selectedItem.value)} className={style.selectSelectedItem}>
                            <SelectedItemContent selectedItem={selectedItem} onRemove={onRemoveItem}/>
                        </span>
                    )}
                </div>}
            {(searchable && !isCombobox && !disabled) && <div className={style.selectQuery}>
                <Input value={query} onChange={setQuery} rightIcon={<i className='fa fa-search'/>}
                    autoFocus/>
            </div>}
            {(selectAll && items.length > 0 && !disabled) &&
                <button type='button' className={style.selectAll} onClick={onToggleAll}>
                    {allSelected ? 'Clear all' : 'Select all'}
                </button>}
            <div className={style.selectItems}>
                <QueryList
                    items={itemsList}
                    activeIndex={activeIndex}
                    onSelect={onSelectItem}
                    filter={isCombobox ? undefined : filterItem}
                    emptyRenderer={<div className={style.selectEmpty}>Empty</div>}
                    itemRenderer={({onClick, item, index, isActive}) =>
                        <div ref={isActive ? activeItemRef : undefined} onClick={onClick}
                            className={cx(style.selectItem, isActive && style.selectItemActive)} key={index}>
                            {item.prefix && <span className={style.selectItemPrefix}>{item.prefix}</span>}
                            {item.label}
                        </div>
                    }
                />
            </div>
        </>} isOpen={open} onToggle={setOpen}
        childrenClassName={cx(className, style.selectMulti)} intent={intent}>
        {isCombobox ? (
            <div className={cx(style.selectSelectedItems, columns && style.selectSelectedItemsColumns)}>
                {selectedItems.length > 0 &&
                    selectedItems.map(selectedItem => (
                        columns
                            ? <div key={String(selectedItem.value)} className={style.selectColumnItem}>
                                <span className={style.selectSelectedItem}>
                                    <SelectedItemContent selectedItem={selectedItem} onRemove={onRemoveItem}/>
                                </span>
                              </div>
                            : <span key={String(selectedItem.value)} className={style.selectSelectedItem}>
                                <SelectedItemContent selectedItem={selectedItem} onRemove={onRemoveItem}/>
                              </span>
                    ))}
                <input
                    ref={comboboxInputRef}
                    className={style.selectMultiInput}
                    autoFocus={autoFocus}
                    value={query}
                    placeholder={selectedItems.length ? '' : placeholder}
                    onChange={event => {
                        setQuery(event.target.value);
                        setOpen(true);
                    }}
                    onKeyDown={handleKeyDown}
                    onClick={event => {
                        event.stopPropagation();
                        setOpen(true);
                    }}
                />
            </div>
        ) : (
            selectedContent
        )}
        {(selectedItems.length > 0 && clearable) &&
        <i className={cx('fa fa-close', style.tagsClear)} onClick={() => onChange([])}/>}
    </DropDown>;
};
